import * as React from 'react'
import { Download, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { DateField } from '@/components/ui/field'
import { Notice } from '@/components/ui/notice'
import { OperationFeedback } from '@/components/visitone/operation-feedback'
import { PageHeader } from '@/components/visitone/page-header'
import { ServerDataTable, type TableCell, type TableRow, type TableState } from '@/components/visitone/server-data-table'
import { RefusedSurface } from '@/components/visitone/surfaces/access-surface'
import { useT } from '@/components/visitone/visitone-provider'
import { useCurrentTenantId } from '@/components/tenant/tenant-context'
import { useAccess } from '@/hooks/use-access'
import { useGuestAccessRecord } from '@/hooks/use-guest-access'
import {
  periodProblems,
  type AccessEvent,
  type AccessPeriod,
  type AccessRecordFile,
  type PeriodProblemCode,
} from '@/lib/bff/guest-access'
import { isForbidden, isNotFound } from '@/lib/bff/guests'
import {
  formatCalendarDate,
  formatDateInput,
  formatLocalTime,
  formatTenantZoneName,
  parseDateInput,
  shiftYears,
  tenantToday,
  toTenantLocal,
} from '@/lib/format/tenant-date'
import { pathForDestination } from '@/lib/routing'

type Errors = { from?: string; to?: string }
type Translate = ReturnType<typeof useT>

const PROBLEM_MESSAGES: Record<PeriodProblemCode, { field: keyof Errors; key: string }> = {
  from_required: { field: 'from', key: 'guestAccess.validation.fromRequired' },
  to_required: { field: 'to', key: 'guestAccess.validation.toRequired' },
  from_outside_retention: { field: 'from', key: 'guestAccess.validation.fromTooEarly' },
  to_in_future: { field: 'to', key: 'guestAccess.validation.toFuture' },
  from_after_to: { field: 'to', key: 'guestAccess.validation.order' },
}

function saveFile({ blob, fileName }: AccessRecordFile) {
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(href), 0)
}

function accessedCell(t: Translate, event: AccessEvent, day: (iso: string) => string): TableCell {
  if (event.period) {
    return {
      primary: t('guestAccess.accessed.accessRecord'),
      secondary: t('guestAccess.accessed.accessRecordPeriod', { from: day(event.period.from), to: day(event.period.to) }),
    }
  }
  if (event.action === 'viewed_guest_record') {
    return {
      primary: t('guestAccess.accessed.guestRecord'),
      secondary: event.guestRecordId ? t('guestAccess.accessed.guestRecordId', { id: event.guestRecordId }) : undefined,
    }
  }
  const count = event.guestCount ?? 0
  return {
    primary: t('guestAccess.accessed.guests', { count }),
    secondary: count === 0 && event.action === 'searched_guests' ? t('guestAccess.accessed.noGuestsSearch') : undefined,
  }
}

function detailOf(t: Translate, event: AccessEvent): TableRow['detail'] {
  const ids = event.customerIds ?? []
  if (event.period || ids.length === 0) return undefined
  const count = event.guestCount ?? ids.length
  const more = count - ids.length
  const list = ids.join(', ')
  return {
    title: t('guestAccess.accessed.detailTitle', { count }),
    description: more > 0 ? t('guestAccess.accessed.detailMore', { ids: list, count: more }) : list,
  }
}

/*
 * Setup › Guest data access (VIS-330): who opened guest data in the current
 * tenant in a period, kept for one year. The route renders this only for a
 * caller holding `guest_access.produce` (Landlords); a 403 or 404 from the BFF
 * still refuses the page. Showing and downloading are recorded productions,
 * so each press of Show record or Download record is a new request.
 *
 * The tenant's zone comes with the record; until one arrives, today and the
 * earliest kept day use the interim fallback zone (TECH-DEBT VIS-327).
 */
export function GuestDataAccessSurface() {
  const t = useT()
  const tenantId = useCurrentTenantId()
  const { tenantName, canOpenSetupHome } = useAccess()
  const record = useGuestAccessRecord()

  const [fromText, setFromText] = React.useState('')
  const [toText, setToText] = React.useState('')
  const [errors, setErrors] = React.useState<Errors>({})
  const [download, setDownload] = React.useState<'idle' | 'busy' | 'failed'>('idle')
  const [status, setStatus] = React.useState('')
  const [downloadTenant, setDownloadTenant] = React.useState(tenantId)
  if (downloadTenant !== tenantId) {
    setDownloadTenant(tenantId)
    setDownload('idle')
    setStatus('')
  }
  const fromRef = React.useRef<HTMLInputElement>(null)
  const toRef = React.useRef<HTMLInputElement>(null)
  const resultRef = React.useRef<HTMLHeadingElement>(null)

  const zone = record.timeZone
  const now = new Date()
  const today = tenantToday(zone, now)
  const earliest = shiftYears(today, -1)
  const day = (iso: string) => formatCalendarDate(iso)
  const zoneSentence = t('guestAccess.period.zone', { tenant: tenantName, zone: formatTenantZoneName(zone) })

  const validate = (): { errors: Errors; period?: AccessPeriod } => {
    const found: Errors = {}
    const from = parseDateInput(fromText)
    const to = parseDateInput(toText)
    const invalid = t('core.dateField.invalid', { example: formatDateInput(today) })
    if (!fromText.trim()) found.from = t('guestAccess.validation.fromRequired')
    else if (!from) found.from = invalid
    else if (from < earliest) found.from = t('guestAccess.validation.fromTooEarly', { earliest: day(earliest) })
    if (!toText.trim()) found.to = t('guestAccess.validation.toRequired')
    else if (!to) found.to = invalid
    else if (to > today) found.to = t('guestAccess.validation.toFuture')
    if (!found.from && !found.to && from && to && from > to) found.to = t('guestAccess.validation.order')
    return found.from || found.to || !from || !to ? { errors: found } : { errors: found, period: { from, to } }
  }

  const focusFirst = (found: Errors) => {
    if (found.from) fromRef.current?.focus()
    else if (found.to) toRef.current?.focus()
  }

  const afterShow = (outcome: Awaited<ReturnType<typeof record.show>>) => {
    if (outcome.status === 'stale') return
    setStatus('')
    if (outcome.status === 'error') {
      const problems = [...periodProblems(outcome.error, 'from'), ...periodProblems(outcome.error, 'to')]
      if (problems.length) {
        const found: Errors = {}
        for (const code of problems) {
          const message = PROBLEM_MESSAGES[code]
          if (message && !found[message.field]) found[message.field] = t(message.key, { earliest: day(earliest) })
        }
        setErrors(found)
        requestAnimationFrame(() => focusFirst(found))
        return
      }
    }
    requestAnimationFrame(() => resultRef.current?.focus())
  }

  const produce = (period: AccessPeriod) => {
    setDownload('idle')
    setStatus(t('guestAccess.result.producingStatus', { from: day(period.from), to: day(period.to) }))
    void record.show(period).then(afterShow)
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (record.status === 'loading') return
    const checked = validate()
    setErrors(checked.errors)
    if (!checked.period) {
      focusFirst(checked.errors)
      return
    }
    produce(checked.period)
  }

  const retry = () => {
    if (record.page === 0 && record.period) {
      setStatus(t('guestAccess.result.producingStatus', { from: day(record.period.from), to: day(record.period.to) }))
    }
    void record.retry().then((outcome) => {
      if (outcome.status !== 'stale') setStatus('')
    })
  }

  const startDownload = async () => {
    if (download === 'busy' || !record.period) return
    setDownload('busy')
    setStatus(t('guestAccess.download.busy'))
    try {
      const outcome = await record.download()
      if (outcome.status === 'stale') return
      saveFile(outcome.file)
      setDownload('idle')
      setStatus(t('guestAccess.download.done', { file: outcome.file.fileName }))
    } catch {
      setDownload('failed')
      setStatus('')
    }
  }

  if (isForbidden(record.error) || isNotFound(record.error)) return <RefusedSurface />

  const refusedPeriod =
    record.status === 'error' &&
    (periodProblems(record.error, 'from').length > 0 || periodProblems(record.error, 'to').length > 0)
  const period = record.period
  const events = record.record?.page.items ?? []
  const state: TableState =
    record.status === 'loading' ? 'loading' : record.status === 'error' ? 'error' : record.total === 0 ? 'empty' : 'data'
  const periodText = period ? { from: day(period.from), to: day(period.to) } : null
  const caption = periodText ? t('guestAccess.table.caption', { tenant: tenantName, ...periodText }) : ''

  const rows: TableRow[] = events.map((event) => {
    const local = toTenantLocal(event.at, zone) ?? event.at
    const when = { date: day(local.slice(0, 10)), time: formatLocalTime(local) }
    const action = t(`guestAccess.actions.${event.action}`)
    const name = event.actor.name ?? event.actor.email ?? t('guestAccess.who.nameNotRecorded')
    return {
      id: event.id,
      label: t('guestAccess.table.rowLabel', { action, name, when: `${when.date}, ${when.time}` }),
      detail: detailOf(t, event),
      cells: {
        when: { primary: when.date, secondary: when.time },
        who: {
          primary: name,
          secondary: event.actor.name ? event.actor.email : undefined,
          pills: [
            event.actor.group === 'visitone_staff'
              ? { tone: 'info', label: t('guestAccess.who.visitOneStaff') }
              : { tone: 'neutral', label: t('guestAccess.who.venueUser') },
          ],
        },
        action: { primary: action },
        accessed: accessedCell(t, event, day),
      },
    }
  })

  return (
    <div className="flex max-w-5xl flex-col gap-4">
      <PageHeader
        breadcrumb={[
          { label: t('setup.title'), to: canOpenSetupHome ? pathForDestination('setup') : undefined },
          t('guestAccess.title'),
        ]}
        title={t('guestAccess.title')}
        summary={t('guestAccess.summary', { tenant: tenantName })}
      />

      <Notice tone="info" icon={<ShieldCheck className="size-5" />} title={t('guestAccess.notice.title')}>
        {t('guestAccess.notice.body')}
      </Notice>

      <Card>
        <CardContent>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4" aria-labelledby="guest-access-period">
            <div>
              <h2 id="guest-access-period" className="text-lg font-semibold text-fg-strong">
                {t('guestAccess.period.heading')}
              </h2>
              <p className="mt-1 text-sm text-fg-muted">{zoneSentence}</p>
            </div>
            <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 lg:max-w-2xl">
              <DateField
                id="guest-access-from"
                label={t('guestAccess.period.from')}
                help={t('guestAccess.period.fromHelp', { earliest: day(earliest) })}
                example={today}
                required
                error={errors.from}
                value={fromText}
                inputRef={fromRef}
                onChange={(text) => {
                  setFromText(text)
                  setErrors((previous) => ({ ...previous, from: undefined }))
                }}
              />
              <DateField
                id="guest-access-to"
                label={t('guestAccess.period.to')}
                help={t('guestAccess.period.toHelp')}
                example={today}
                required
                error={errors.to}
                value={toText}
                inputRef={toRef}
                onChange={(text) => {
                  setToText(text)
                  setErrors((previous) => ({ ...previous, to: undefined }))
                }}
              />
            </div>
            <div>
              <Button variant="primary" type="submit" aria-disabled={record.status === 'loading' || undefined}>
                {t('guestAccess.period.show')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <p role="status" className="sr-only">
        {status}
      </p>

      {period && periodText && record.status !== 'idle' && !refusedPeriod ? (
        <section aria-labelledby="guest-access-result" className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div className="min-w-0">
              <h2
                id="guest-access-result"
                ref={resultRef}
                tabIndex={-1}
                className="text-lg font-semibold text-fg-strong outline-none"
              >
                {t('guestAccess.result.heading', periodText)}
              </h2>
              {state === 'data' ? (
                <p className="mt-1 text-sm text-fg-muted">
                  {t('guestAccess.result.count', { count: record.total, tenant: tenantName })} {zoneSentence}
                </p>
              ) : null}
              {state === 'data' && period.from === earliest ? (
                <p className="mt-1 text-sm text-fg-muted">
                  {t('guestAccess.result.boundary', {
                    earliest: day(earliest),
                    time: formatLocalTime(toTenantLocal(now.toISOString(), zone) ?? ''),
                  })}
                </p>
              ) : null}
              {state === 'data' || state === 'empty' ? (
                <p className="mt-1 text-sm text-fg">{t('guestAccess.result.scope')}</p>
              ) : null}
            </div>
            {state === 'data' ? (
              <Button variant="secondary" aria-disabled={download === 'busy' || undefined} onClick={() => void startDownload()}>
                <Download />
                {download === 'busy' ? t('guestAccess.download.busy') : t('guestAccess.download.action')}
              </Button>
            ) : null}
          </div>

          {download === 'failed' ? (
            <OperationFeedback
              state="error"
              title={t('guestAccess.download.failedTitle')}
              detail={t('guestAccess.download.failedBody')}
            />
          ) : null}

          <ServerDataTable
            state={state}
            onRetry={retry}
            columns={[
              { key: 'when', label: t('guestAccess.table.when'), width: '8rem' },
              { key: 'who', label: t('guestAccess.table.who'), width: '14rem' },
              { key: 'action', label: t('guestAccess.table.action'), width: '10rem' },
              { key: 'accessed', label: t('guestAccess.table.accessed'), width: '11rem' },
            ]}
            rows={rows}
            selectable={false}
            sortedBy={{ key: 'when', direction: 'desc' }}
            caption={caption}
            regionLabel={caption}
            footer={
              state === 'loading'
                ? t('guestAccess.result.producing')
                : state === 'data'
                  ? t('guestAccess.result.footer', { count: record.total })
                  : undefined
            }
            pagination={
              state === 'data'
                ? { page: record.page, totalPages: record.totalPages, onPageChange: (page) => void record.goToPage(page) }
                : undefined
            }
            loadingRowCount={5}
            messages={{
              loadErrorTitle: t('guestAccess.loadFailed.title'),
              loadErrorBody: t('guestAccess.loadFailed.body'),
              retry: t('guestAccess.loadFailed.retry'),
              retryVariant: 'secondary',
            }}
            empty={{
              title: t('guestAccess.empty.title'),
              body: t('guestAccess.empty.body', { tenant: tenantName, ...periodText }),
            }}
          />
        </section>
      ) : null}
    </div>
  )
}
